/**
 * Embedded inside Shopify.js's tab area. Receives storeId from parent;
 * renders a paginated orders table with its own status filter + search.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, ChevronLeft, ChevronRight, Package } from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './Shopify.css';

const moneyFmt = (v, code) => {
  if (v == null) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: code || 'USD', maximumFractionDigits: 0,
    }).format(Number(v));
  } catch { return `${code || ''} ${Number(v).toLocaleString()}`; }
};
const dateFmt = (iso) => iso ? new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const ShopifyOrders = ({ storeId }) => {
  const { showError } = useToast();

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  // Reset to page 1 if the parent's storeId changes
  useEffect(() => { setPage(1); }, [storeId]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: pageSize, sortBy: 'newest' });
      if (storeId) params.set('storeId', storeId);
      if (search) params.set('search', search);
      if (status) params.set('status', status);
      const res = await api.get(`/api/shopify/orders?${params}`);
      setRows(res.data.orders || []);
      setPagination(res.data.pagination || { total: 0, totalPages: 1 });
    } catch (e) {
      showError('Failed to load orders: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, storeId, search, status, showError]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="shop-panel">
      <div className="shop-tab-toolbar">
        <select className="shop-select" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          <option value="PAID">Paid</option>
          <option value="PARTIALLY_REFUNDED">Partially refunded</option>
          <option value="REFUNDED">Refunded</option>
          <option value="PENDING">Pending</option>
        </select>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }} />
          <input
            className="shop-select"
            style={{ paddingLeft: 32, width: '100%' }}
            placeholder="Search order # or customer…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="shop-loading" style={{ minHeight: 200 }}><div className="shop-spinner" /></div>
      ) : rows.length === 0 ? (
        <div className="shop-empty">
          <Package size={32} style={{ opacity: 0.4 }} />
          <div style={{ marginTop: '0.5rem' }}>No orders found.</div>
        </div>
      ) : (
        <>
          <table className="shop-table">
            <thead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Total</th>
                <th>Refunded</th>
                <th>Financial</th>
                <th>Fulfillment</th>
                <th>City</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(o => (
                <motion.tr key={o.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.15 }}>
                  <td><Link to={`/shopify/orders/${o.id}`} className="shop-order-link">{o.name}</Link></td>
                  <td>
                    <div className="shop-order-customer">
                      {o.customer_display_name || '—'}
                      {o.customer_email && <div className="shop-order-customer-sub">{o.customer_email}</div>}
                    </div>
                  </td>
                  <td>{moneyFmt(o.total_price, o.currency_code)}</td>
                  <td style={{ color: o.total_refunded > 0 ? '#f87171' : 'rgba(255,255,255,0.4)' }}>
                    {o.total_refunded > 0 ? moneyFmt(o.total_refunded, o.currency_code) : '—'}
                  </td>
                  <td><span className={`shop-badge ${(o.display_financial_status || '').toLowerCase()}`}>{o.display_financial_status || '—'}</span></td>
                  <td><span className={`shop-badge ${(o.display_fulfillment_status || '').toLowerCase()}`}>{o.display_fulfillment_status || '—'}</span></td>
                  <td>{o.city_tag || o.shipping_city || '—'}</td>
                  <td className="shop-cell-time">{dateFmt(o.shopify_created_at)}</td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          <div className="shop-pagination">
            <div>
              Show <select className="shop-select" style={{ display: 'inline-block', padding: '0.25rem 0.5rem' }} value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select> per page
            </div>
            <div className="shop-pagination-controls">
              <button className="shop-page-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft size={14} /> Prev</button>
              <span className="shop-page-info">Page {page} of {pagination.totalPages || 1}</span>
              <button className="shop-page-btn" disabled={page >= (pagination.totalPages || 1)} onClick={() => setPage(p => p + 1)}>Next <ChevronRight size={14} /></button>
            </div>
            <div className="shop-page-total">{pagination.total.toLocaleString()} orders total</div>
          </div>
        </>
      )}
    </div>
  );
};

export default ShopifyOrders;
